import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import * as signalR from "@microsoft/signalr"
import { AuthService } from './auth.service';
import { environment } from 'src/environments/environment';

export interface SocketMessageData<T>{
  data: T
  sender?: string
  eventName?: string
  complete?: boolean
}

export interface ISocketUser{
  userId: number
  connectionId?: string
  firstName: string
  lastName: string
}

export interface SocketNewWorkstep{
  workstepId: number
  contentId: number
}

export interface SocketChatMessage{
  message: string
  timestamp: Date
  fullname: string
  fromSelf: boolean
}

export class SocketUser implements ISocketUser{
  
  connectionId?: string;
  userId: number;
  firstName: string;
  lastName: string;

  public chat: SocketChatMessage[] = []

  constructor(firstName: string, lastName: string, id: number, connectionId?: string){
    this.firstName = firstName
    this.lastName = lastName
    this.userId = id,
    this.connectionId = connectionId
  }

  public static initByObject(obj: ISocketUser): SocketUser{
    return new SocketUser(obj.firstName, obj.lastName, obj.userId, obj?.connectionId)
  }

  public get fullName(): string{
    return `${this.firstName} ${this.lastName}`
  }

  public get initials(): string{
    return `${this.firstName?.charAt(0)}${this.lastName?.charAt(0)}`
  }

}


@Injectable({
  providedIn: 'root'
})
export class SocketsService {

  public connection: signalR.HubConnection;

  public get socketState(): signalR.HubConnectionState{
    return this.connection?.state || signalR.HubConnectionState.Disconnected
  }

  public serverVersionGreaterThan: (checkVersion: string) => boolean

  constructor(
    private http: HttpService,
    private authSrv: AuthService
  ) {
    this.authSrv.isDemoMode = this.http.getbaseUrl == environment.demoServer
   }

  /**
   * Initialisiert den Socket.
   * 
   * @returns Eine Promise, die angibt, ob die Initialisierung erfolgreich war oder nicht.
   */
  public async initializeSocket() {
    
    if(this.authSrv.isDemoMode) return
    if(!this.serverVersionGreaterThan("1.9.0") || this.authSrv.isDemoMode){
      console.warn("Server version ist nicht größer als 1.9.0 oder Sie befinden sich in Demo-Modus, keine Websockets verfügbar.");
      return
    }
    try {
      if(this.socketState === signalR.HubConnectionState.Connected) this.connection.stop()
      this.connection = null
      
      this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.http.getbaseUrl}ws`, {withCredentials: false, accessTokenFactory: () => {
        return this.http.token
      }})
      .withAutomaticReconnect()
      .build();


      await this.connection.start()
      var connectionId = await this.connection.invoke("GetConnectionId")
      sessionStorage.setItem("connectionId", connectionId)  

      //Fehlerbehandlung
      this.connection.onclose(async (error) => {
        console.error("Verbindung wurde geschlossen", error)
      })

    } catch (error) {
      console.error(error.toString())
    }
  }

  /**
   * Registriert einen Handler für eine bestimmte Methode.
   * 
   * @param method - Die Methode, für die der Handler registriert werden soll.
   * @param handler - Die Funktion, die als Handler für die Methode fungiert.
   * @template T - Der Typ der Daten, die der Handler akzeptiert.
   */
  public registerHandler<T>(method: string, handler: (data: SocketMessageData<T>) => void){
    if(!this.connection) return
    this.connection.on(method, (result: string | T)=> {
      if(typeof result == "string") handler(JSON.parse(result) as SocketMessageData<T>)
      else handler(result as SocketMessageData<T>)
    })
  }

  /**
   * Fügt eine Socket-Gruppe hinzu und registriert einen Handler für eingehende Daten.
   * 
   * @param group - Der Name der Socket-Gruppe.
   * @param handler - Der Handler, der aufgerufen wird, wenn Daten empfangen werden.
   * @template T - Der Typ der empfangenen Daten.
   */
  public joinTicketGroup<T>(group: string, handler: (data: SocketMessageData<T>) => void){
    if(!this.connection) return
    this.connection.invoke("JoinTicketGroup", group)
    this.connection.on(group, (result: SocketMessageData<T>)=> handler(result))
  }

  /**
   * Verlässt eine Gruppe.
   * 
   * @param group - Der Name der Gruppe, die verlassen werden soll.
   */
  public leaveGroup(group: string){
    if(!this.connection) return
    this.connection.invoke("LeaveGroup", group) 
    this.removeHandler(group)
  }

  /**
   * Entfernt den Handler für die angegebene Methode.
   * 
   * @param method - Die Methode, für die der Handler entfernt werden soll.
   */
  public removeHandler<T>(method: string){
    if(!this.connection) return
    this.connection.off(method)
  }

  /**
   * Beendet die Verbindung zum SignalR-Hub, falls die Verbindung besteht.
   */
  public async stopConnection(){
    if(!this.connection) return
    if(this.socketState === signalR.HubConnectionState.Connected) await this.connection.stop()
  }

  /**
   * Ruft eine Methode auf dem Socket-Verbindungsserver auf.
   * 
   * @param method - Der Name der Methode, die aufgerufen werden soll.
   * @param args - Die Argumente, die an die Methode übergeben werden sollen.
   * @returns Ein Promise, das das Ergebnis der Methode enthält.
   */
  public async invoke(method: string, ...args: any[]){
    if(!this.connection) return
    return await this.connection.invoke(method, ...args)
  }


}
